import React, { Component, PropTypes } from 'react';
import ReactSelect from 'react-select';
import cx from 'classnames';
import _ from 'underscore';
import TeamOption from '../options/TeamOption';
import { compareStrings } from 'strike-a-match';
import { mean, std } from 'mathjs';
import { tagEvent } from '../../util/tracking/localyticsHelper';

const SOURCE = { Source: 'TourneyTeamSelect' };

const DivisionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
});

const TeamShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  pool: PropTypes.shape({
    division: DivisionShape.isRequired
  })
});

const filterTeam = (team, filter) => {
  if (filter.trim().length === 0) {
    return true;
  }

  filter = filter.trim().toUpperCase();

  const { name, pool: { division } } = team;
  const upperName = name.toUpperCase();
  const upperDivisionName = division ? division.name.toUpperCase() : '';

  return _.all(filter.split(' '), (searchTerm) => {
    return searchTerm.length === 0 ||
      (upperName.indexOf(searchTerm) !== -1) ||
      (upperDivisionName.indexOf(searchTerm) !== -1);
  });
};

export default class TourneyTeamSelect extends Component {
  static propTypes = {
    autosize: PropTypes.bool,
    showNumGames: PropTypes.bool,
    teams: PropTypes.arrayOf(TeamShape).isRequired,
    suggestionsFor: PropTypes.string,
    disabled: PropTypes.bool,
    renderAcronyms: PropTypes.bool,
    required: PropTypes.bool,
    multi: PropTypes.bool,
    placeholder: PropTypes.string,
    useCache: PropTypes.bool,
    numSuggestions: PropTypes.number,
    suggestionDeviations: PropTypes.number
  };

  static defaultProps = {
    autosize: false,
    suggestionsFor: null,
    disabled: false,
    required: true,
    renderAcronyms: false,
    multi: true,
    useCache: true,
    placeholder: null,
    numSuggestions: 5,
    suggestionDeviations: 2
  };

  state = {
    expanded: false
  };

  handleSuggestionClick(team) {
    const { onChange, multi } = this.props;
    if (team) {
      tagEvent('recruit_suggested_tourney_team_clicked', () => ({ ...SOURCE, team: team.name }));
    }

    if (multi) {
      onChange([ team ]);
    } else {
      onChange(team);
    }
  }

  getClasses() {
    const { className, disabled, required } = this.props;
    return cx({
      'Select-control--danger': required && !disabled && !this.hasValue()
    }, className);
  }

  getSuggestions() {
    const { teams, suggestionsFor, numSuggestions, suggestionDeviations } = this.props;
    const { expanded } = this.state;

    if (typeof suggestionsFor !== 'string' ||
      suggestionsFor.trim().length === 0 ||
      this.hasValue() ||
      teams.length === 0) {
      return null;
    }

    // what we will try to match
    const matching = suggestionsFor.trim().toUpperCase();

    // this is the function that gives a score for the match
    const scoringFunction = (name) => compareStrings(name, matching);

    // get a score for all the teams
    const teamsWithScore = _.map(teams, (team) => {
      const { name } = team;

      return {
        team,
        score: scoringFunction(name)
      };
    });

    const scores = _.map(teamsWithScore, (tws) => tws.score);
    const averageScore = mean(scores);
    const stdDev = std(scores);
    const minimumScore = (suggestionDeviations * stdDev) + averageScore;

    const filteredByScore = _.filter(teamsWithScore, (tws) => tws.score > minimumScore);
    const sortedSuggestions = _.sortBy(filteredByScore, (tws) => (1 - tws.score));

    if (sortedSuggestions.length === 0) {
      return null;
    }

    let numToShow = numSuggestions;
    if (expanded) {
      numToShow = sortedSuggestions.length;
    }

    let expandEllipsis = null;
    if (!expanded && numToShow < sortedSuggestions.length) {
      expandEllipsis = <a onClick={() => this.setState({ expanded: true })}>&hellip;</a>;
    }

    return (
      <div className="pd-sm sm-font">
        <strong key="s">Possibly: </strong>
        <em>
          {
            _.first(sortedSuggestions, numToShow).map((teamWithSuggestion, index) => {
              const team = teamWithSuggestion.team;
              const { name, pool: { division } } = team;

              return (
                <span key={team.id}>
                  <a key="a" onClick={() => this.handleSuggestionClick(team)}>
                    {name} ({division.name})
                  </a>
                  { index < sortedSuggestions.length - 1 ? ', ' : null }
                </span>
              );
            })
          }
          {
            expandEllipsis
          }
        </em>
      </div>
    );
  }

  hasValue() {
    const { value, multi } = this.props;

    if (multi) {
      return (_.isArray(value) && value.length > 0);
    } else {
      return Boolean(value);
    }
  }

  renderTeam = (team) => {
    const { renderAcronyms, showNumGames } = this.props;
    return <TeamOption showNumGames={showNumGames} renderAcronym={renderAcronyms} team={team}/>;
  };

  handleChange = value => {
    const { onChange, multi } = this.props;
    if (!multi && _.isArray(value)) {
      onChange(null);
    } else {
      onChange(value);
    }
  };

  render() {
    const {
      teams,
      disabled,
      placeholder,
      ...rest
    } = this.props;

    return (
      <div className={this.getClasses()}>
        <ReactSelect
          key="select"
          options={teams}
          minimumInput={1}
          filterOption={filterTeam}
          optionRenderer={this.renderTeam}
          valueKey="id"
          disabled={disabled}
          placeholder={placeholder || 'Select Tourney Team'}
          valueRenderer={this.renderTeam}
          {...rest}
          onChange={this.handleChange}
        />
        {!disabled ? <div key="sugg">{this.getSuggestions()}</div> : null}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/components/inputs/TourneyTeamSelect.jsx